import { Money } from "../types/money";
import { Currency } from "../types/enums/currency";
import {
  addMoney,
  subtractMoney,
  multiplyMoney,
  divideMoney,
  absMoney,
  roundMoney,
  zeroMoney,
} from "./money";
import { MultiCurrencyMoney } from "../types/multiCurrencyMoney";

export const addMultiCurrencyMoney = (
  multiMoney: MultiCurrencyMoney,
  addend: MultiCurrencyMoney | Money,
): MultiCurrencyMoney => {
  const result: Partial<Record<Currency, Money | undefined>> = {
    ...multiMoney.currencies,
  };

  if ("currencies" in addend) {
    Object.keys(addend.currencies).forEach((key) => {
      const currency = key as Currency;
      const addendMoney = addend.currencies[currency];
      if (addendMoney) {
        if (result[currency]) {
          result[currency] = addMoney(result[currency], addendMoney);
        } else {
          result[currency] = addendMoney;
        }
      }
    });
  } else {
    const currency = addend.currency;
    if (result[currency]) {
      result[currency] = addMoney(result[currency], addend);
    } else {
      result[currency] = addend;
    }
  }

  return { currencies: result };
};

export const subtractMultiCurrencyMoney = (
  multiMoney: MultiCurrencyMoney,
  subtrahend: MultiCurrencyMoney | Money,
): MultiCurrencyMoney => {
  const result: Partial<Record<Currency, Money | undefined>> = {
    ...multiMoney.currencies,
  };

  if ("currencies" in subtrahend) {
    Object.keys(subtrahend.currencies).forEach((key) => {
      const currency = key as Currency;
      const subtrahendMoney = subtrahend.currencies[currency];
      if (subtrahendMoney) {
        if (result[currency]) {
          result[currency] = subtractMoney(result[currency], subtrahendMoney);
        } else {
          result[currency] = subtractMoney(
            zeroMoney(currency),
            subtrahendMoney,
          );
        }
      }
    });
  } else {
    const currency = subtrahend.currency;
    if (result[currency]) {
      result[currency] = subtractMoney(result[currency], subtrahend);
    } else {
      result[currency] = subtractMoney(zeroMoney(currency), subtrahend);
    }
  }

  return { currencies: result };
};

export const multiplyMultiCurrencyMoney = (
  multiMoney: MultiCurrencyMoney,
  multiplier: number,
): MultiCurrencyMoney => {
  const result: Partial<Record<Currency, Money | undefined>> = {
    ...multiMoney.currencies,
  };

  Object.keys(multiMoney.currencies).forEach((key) => {
    const currency = key as Currency;
    const money = multiMoney.currencies[currency];
    if (money) {
      result[currency] = multiplyMoney(money, multiplier);
    }
  });

  return { currencies: result };
};

export const divideMultiCurrencyMoney = (
  multiMoney: MultiCurrencyMoney,
  divisor: number,
): MultiCurrencyMoney => {
  const result: Partial<Record<Currency, Money | undefined>> = {
    ...multiMoney.currencies,
  };

  Object.keys(multiMoney.currencies).forEach((key) => {
    const currency = key as Currency;
    const money = multiMoney.currencies[currency];
    if (money) {
      result[currency] = divideMoney(money, divisor);
    }
  });

  return { currencies: result };
};

export const absMultiCurrencyMoney = (
  multiMoney: MultiCurrencyMoney,
): MultiCurrencyMoney => {
  const result: Partial<Record<Currency, Money | undefined>> = {
    ...multiMoney.currencies,
  };

  Object.keys(multiMoney.currencies).forEach((key) => {
    const currency = key as Currency;
    const money = multiMoney.currencies[currency];
    if (money) {
      result[currency] = absMoney(money);
    }
  });

  return { currencies: result };
};

export const roundMultiCurrencyMoney = (
  multiMoney: MultiCurrencyMoney,
): MultiCurrencyMoney => {
  const result: Partial<Record<Currency, Money | undefined>> = {
    ...multiMoney.currencies,
  };

  Object.keys(multiMoney.currencies).forEach((key) => {
    const currency = key as Currency;
    const money = multiMoney.currencies[currency];
    if (money) {
      result[currency] = roundMoney(money);
    }
  });

  return { currencies: result };
};

export const getAmountByCurrency = (
  multiMoney: MultiCurrencyMoney,
  currency: Currency,
): Money | undefined => {
  return multiMoney.currencies[currency];
};

export const zeroMultiCurrencyMoney = (): MultiCurrencyMoney => {
  const result: Partial<Record<Currency, Money | undefined>> = {};

  return { currencies: result };
};
